/** Plan 03-15 — NotificationToastService.
 *
 * Globally provided service that subscribes to NotificationHubService.push$ (Plan 03-12)
 * and surfaces every incoming push as a MatSnackBar. Bootstrapped from AppComponent.ngOnInit
 * (after app.config.ts's APP_INITIALIZER chain has connected the hub).
 *
 * NOTIF-04 in-app channel completion:
 *   Notification Function (Plan 03-10) → Service Bus → SignalR Service → NotificationHubService
 *   (Plan 03-12) → push$ → THIS service → MatSnackBar → user sees toast.
 *
 * Per-tab dedup is ALREADY performed by NotificationHubService (Plan 03-12 RESEARCH §Pitfall 5).
 * We keep an additional bounded `seenCorrelationIds` Set here as defense-in-depth: if the hub's
 * dedup ever gets bypassed (e.g., a future refactor removes it, or a malformed payload sneaks
 * past), the toast service still won't surface duplicate snackbars on the same tab.
 *
 * Action label mapping per event type:
 *   - action-required events (rejected / approved / ready_for_*_signoff) → 'View' action navigates
 *     to /submissions/{id} via the linkUrl on the push payload
 *   - informational events (validation_complete / window_* / identity_exception_summary) → no
 *     action button; toast auto-dismisses on duration
 */

import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { NotificationHubService } from './notification-hub.service';
import type { NotificationPush } from '../models/notification-push';

/** UI-SPEC §"Empty / Loading / Error / Success States" → toast duration for in-app pushes. */
export const TOAST_DURATION_MS = 8000;

/** Capacity of the per-tab dedup Set (defense-in-depth above the hub's own dedup). */
export const SEEN_CORRELATION_ID_CAPACITY = 200;

/** Action-required event types — these get a 'View' action button on the snackbar. */
const ACTION_REQUIRED_EVENT_TYPES: ReadonlySet<string> = new Set([
  'rejected',
  'approved',
  'ready_for_school_signoff',
  'ready_for_district_signoff',
  'ready_for_state_signoff',
]);

@Injectable({ providedIn: 'root' })
export class NotificationToastService implements OnDestroy {
  private readonly hub = inject(NotificationHubService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private subscription?: Subscription;

  /**
   * Per-tab dedup Set — bounded with insertion-order LRU eviction. Same pattern as
   * NotificationHubService (Plan 03-12 Pitfall 5 mitigation); we keep our own copy as
   * defense-in-depth because the hub may be replaced or bypassed in test/dev scenarios.
   */
  private readonly seenCorrelationIds = new Set<string>();

  /**
   * Begin subscribing to hub.push$ — idempotent. Called once from AppComponent.ngOnInit
   * (after app.config.ts's APP_INITIALIZER chain has finished bootstrap + hub connect).
   */
  start(): void {
    if (this.subscription) return;
    this.subscription = this.hub.push$.subscribe((push: NotificationPush) =>
      this.handlePush(push),
    );
  }

  /** Tear down. Idempotent. */
  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /**
   * Render one push as a snackbar. SignalR callbacks may run outside Angular's NgZone, so we
   * wrap MatSnackBar.open in zone.run to ensure change detection fires for the action button +
   * dismissal.
   */
  private handlePush(push: NotificationPush): void {
    if (!push || typeof push.correlationId !== 'string' || push.correlationId.length === 0) {
      return;
    }

    if (this.seenCorrelationIds.has(push.correlationId)) {
      return;
    }
    if (this.seenCorrelationIds.size >= SEEN_CORRELATION_ID_CAPACITY) {
      const oldest = this.seenCorrelationIds.values().next().value;
      if (oldest !== undefined) {
        this.seenCorrelationIds.delete(oldest);
      }
    }
    this.seenCorrelationIds.add(push.correlationId);

    const headline = push.message && push.message.length > 0 ? push.message : push.subject;
    const action = this.actionLabelFor(push.eventType);

    this.zone.run(() => {
      const ref = this.snackBar.open(headline, action, {
        duration: TOAST_DURATION_MS,
        politeness: 'polite',
        panelClass: 'nepf-notification-snackbar',
      });
      if (action) {
        ref.onAction().subscribe(() => this.navigateFor(push));
      }
    });
  }

  /** Return 'View' for action-required events; '' for informational events. */
  private actionLabelFor(eventType: string): string {
    return ACTION_REQUIRED_EVENT_TYPES.has(eventType) ? 'View' : '';
  }

  /**
   * Navigate to the deep link the dispatcher computed (BuildLinkUrl in Plan 03-12). The link
   * is typically /submissions/{id} for submission-scoped events, or /identity/exceptions /
   * /submissions for window + identity events.
   */
  private navigateFor(push: NotificationPush): void {
    const target = push.linkUrl && push.linkUrl.length > 0 ? push.linkUrl : '/submissions';
    void this.router.navigateByUrl(target);
  }
}
